import _ from 'lodash';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { AddIcon } from '@treams/treams-icons';
import { NotesSidebarUserFragment, useNotesSidebarPeopleQuery } from '@treams/treams-network';
import {
	Avatar,
	Button,
	FlexGap,
	Label,
	Popover,
	Sidebar,
	Skeleton,
	UserSelect,
} from '@treams/treams-ui';

type Props = {
	recurseManagees: boolean;
	excludeUserIDs?: string[];
};

type Group = 'managees' | 'other';

const GroupTitle = styled(Label)`
	display: block;
	padding-left: 0;
`;

const NotesSidebarPeople: FC<Props> = ({ recurseManagees, excludeUserIDs = [] }) => {
	const { t } = useTranslation();

	const { data } = useNotesSidebarPeopleQuery({
		variables: { recurse: recurseManagees },
	});

	const peopleByGroup = useMemo(() => {
		if (!data) return {};

		const subjectIDs = data.noteSubjectsWithCount.map((subject) => subject.user.id);
		const manageesIDs = data.viewer.managees.map((user) => user.id);

		const nonSubjects = data.viewer.managees.filter((user) => !subjectIDs.includes(user.id));
		const allPeople = _.uniqBy(
			data.noteSubjectsWithCount.concat(
				nonSubjects.map((user) => ({
					user,
					count: 0,
				})),
			),
			(subject) => subject.user.id,
		);

		const filteredPeople = allPeople.filter((subject) => !excludeUserIDs.includes(subject.user.id));

		return _.groupBy(filteredPeople, (subject): Group => {
			if (manageesIDs.includes(subject.user.id)) {
				return 'managees';
			}

			return 'other';
		});
	}, [data, excludeUserIDs]);

	const relevantGroups = useMemo<Group[]>(() => {
		const baseGroups: Group[] = ['managees', 'other'];

		if (!data) return baseGroups;

		// Include groups with people; always show the "other" group
		return baseGroups.filter((group) => peopleByGroup[group]?.length > 0 || group === 'other');
	}, [data, peopleByGroup]);

	const existingPeopleIDs = useMemo(() => {
		return _.uniq(
			Object.values(peopleByGroup).flatMap((group) => group.map((subject) => subject.user.id)),
		);
	}, [peopleByGroup]);

	return (
		<Box mb={3}>
			{relevantGroups.map((group) => (
				<Sidebar.Section
					key={group}
					title={
						<GroupTitle mb={1}>
							{t(`oneOnOnes.sidebar.groups.${relevantGroups.length > 1 ? group : 'colleagues'}`)}
						</GroupTitle>
					}
				>
					<FlexGap flexDirection="column">
						{data ? (
							<>
								{peopleByGroup[group]?.map((subject) => (
									<SidebarUser key={subject.user.id} member={subject.user} count={subject.count} />
								))}
								{group === 'other' && <SidebarAddUserButton excludeIDs={existingPeopleIDs} />}
							</>
						) : (
							<Skeleton.Repeat count={3}>
								<SidebarUserSkeleton />
							</Skeleton.Repeat>
						)}
					</FlexGap>
				</Sidebar.Section>
			))}
		</Box>
	);
};

export default NotesSidebarPeople;

type SidebarUserProps = {
	member: NotesSidebarUserFragment;
	count: number;
};

const AVATAR_SIZE = 24;

const SidebarUser = ({ member, count }: SidebarUserProps) => {
	return (
		<Sidebar.Item
			small
			key={member.id}
			icon={<Avatar user={member} size={AVATAR_SIZE} />}
			title={member.fullName}
			path={`/notes/subject/${member.id}`}
			badge={count === 0 ? '—' : count}
			exact
		/>
	);
};

const SidebarUserSkeleton = () => {
	return (
		<Box p="8px 12px" mb="2px" minHeight={40}>
			<Flex alignItems="center">
				<Box flexGrow={0} flexShrink={0} mr={2}>
					<Avatar.Skeleton size={AVATAR_SIZE} />
				</Box>
				<Skeleton.Box flexGrow={1} height={16} />
			</Flex>
		</Box>
	);
};

const SidebarAddUserButton = ({ excludeIDs }: { excludeIDs: string[] }) => {
	const { t } = useTranslation();
	const [visible, setVisible] = useState(false);

	const history = useHistory();

	return (
		<Box my={2}>
			<Popover.Target
				visible={visible}
				contents={
					<UserSelect.Menu
						excluded={excludeIDs}
						setSelected={(userID) => {
							history.push(`/notes/new/subject/${userID}`);
						}}
					/>
				}
				onDismiss={() => setVisible(false)}
				autofocus={true}
			>
				<Button
					type="secondary"
					icon={<AddIcon />}
					title={t('oneOnOnes.sidebar.addSubject')}
					onClick={() => setVisible(true)}
					width="100%"
				/>
			</Popover.Target>
		</Box>
	);
};
